import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { Check } from "@material-symbols-svg/react/rounded/icons/check";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/components/ui/button";
import { Field } from "@/shared/components/ui/field";
import { useDocumentTitle } from "@/shared/i18n";
import { AuthCard } from "./auth-card";
import {
  AuthFieldError,
  AuthFieldLabel,
  AuthHeading,
  AuthSubmitButton,
  PasswordInput,
} from "./auth-fields";
import { AuthStatusPanel } from "./auth-status-panel";
import { meetsAllPasswordCriteria } from "./password-criteria";
import { PasswordCriteriaChecklist } from "./password-criteria-checklist";
import { type NewPasswordInput, newPasswordSchema } from "./schema";

const FORM_ID = "change-password";
const PASSWORD_ID = `${FORM_ID}-password`;
const CRITERIA_ID = `${PASSWORD_ID}-criteria`;
const ERROR_ID = `${PASSWORD_ID}-error`;

type ChangePasswordScreenProps = {
  /** Commits the new password (confirmPasswordReset); rejects with a
   *  display-ready message on failure (expired/invalid code, weak password). */
  onConfirm: (newPassword: string) => Promise<void>;
  /** Navigates back to the app/login. Omitted (e.g. no resolvable tenant
   *  app URL) -> the success panel renders without the button. */
  onBackToLogin?: () => void;
};

/** New-password form with live criteria checklist, swaps to the success panel. */
export function ChangePasswordScreen({
  onConfirm,
  onBackToLogin,
}: ChangePasswordScreenProps) {
  const { t } = useTranslation("auth");
  useDocumentTitle(t("pageTitles.changePassword"));
  const [changed, setChanged] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<NewPasswordInput>({
    resolver: standardSchemaResolver(newPasswordSchema),
    defaultValues: { password: "" },
    mode: "onSubmit",
  });

  const password = form.watch("password");
  const isValid = meetsAllPasswordCriteria(password);
  const isSubmitting = form.formState.isSubmitting;
  // After a submit attempt the unmet criteria switch from neutral to error red.
  const attempted = form.formState.submitCount > 0;

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      await onConfirm(values.password);
      setChanged(true);
    } catch (error) {
      // A rejection without a message, or an Error with an empty one, is still
      // a failed request; the learner reads that, not the submit button's own
      // label.
      setSubmitError(
        error instanceof Error && error.message
          ? error.message
          : t("errors.unexpected"),
      );
    }
  });

  if (changed) {
    return (
      <AuthStatusPanel
        icon={Check}
        title={t("changePassword.changed.title")}
        description={t("changePassword.changed.description")}
      >
        {onBackToLogin ? (
          <Button
            type="button"
            variant="ghost"
            onClick={onBackToLogin}
            className="h-10 w-full rounded-full bg-(--schemes-surface-container-high) text-label-small text-muted-foreground hover:text-muted-foreground"
          >
            {t("changePassword.changed.backToLogin")}
          </Button>
        ) : null}
      </AuthStatusPanel>
    );
  }

  return (
    <AuthCard>
      <AuthHeading
        title={t("changePassword.title")}
        description={t("changePassword.description")}
      />

      <form
        id={FORM_ID}
        onSubmit={onSubmit}
        noValidate
        className="flex flex-col gap-6"
      >
        <div className="flex flex-col gap-4">
          <Controller
            name="password"
            control={form.control}
            render={({ field }) => (
              <Field>
                <AuthFieldLabel htmlFor={PASSWORD_ID}>
                  {t("signIn.passwordLabel")}
                </AuthFieldLabel>
                <PasswordInput
                  {...field}
                  id={PASSWORD_ID}
                  autoComplete="new-password"
                  aria-describedby={CRITERIA_ID}
                />
              </Field>
            )}
          />

          <PasswordCriteriaChecklist
            id={CRITERIA_ID}
            password={password}
            attempted={attempted}
          />

          {submitError && (
            <AuthFieldError id={ERROR_ID}>{submitError}</AuthFieldError>
          )}
        </div>

        <AuthSubmitButton active={isValid} submitting={isSubmitting}>
          {t("changePassword.submit")}
        </AuthSubmitButton>
      </form>
    </AuthCard>
  );
}
